/*
 * Strings for the recurring schedule feature (features/cmmsRecurringSchedules).
 *
 * Shared verbs and the enum label helpers (frequency, day of week, work order type
 * and priority) live in features/cmms/cmmsMessages.js.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms.recurringschedules";

export default defineMessages({
  // page
  pageTitle: { id: `${scope}.page.title`, defaultMessage: "Recurring schedules" },
  pageSubtitle: {
    id: `${scope}.page.subtitle`,
    defaultMessage: "Manage PM schedules. The nightly sweep generates work orders when NextRunDate has passed.",
  },
  addSchedule: { id: `${scope}.page.addSchedule`, defaultMessage: "Add schedule" },

  // filters + grid columns
  filterActive: { id: `${scope}.filter.active`, defaultMessage: "Active" },
  // Names the fields it covers, like the register's box and the work order list's — a bare
  // "Search" leaves someone guessing whether the asset is included. It replaced an asset
  // dropdown, so saying that it is, is most of the point.
  filterSearch: {
    id: `${scope}.filter.search`,
    defaultMessage: "Search by title, description, asset or tool",
  },
  // Stat tile captions are upper-cased by CSS (textTransform), not by the message.
  // Shouting at translators forces them to guess whether the caps are meaningful, and
  // some locales have no meaningful upper case at all.
  statActive: { id: `${scope}.stat.active`, defaultMessage: "Active" },
  filterAll: { id: `${scope}.filter.all`, defaultMessage: "All" },
  filterClear: { id: `${scope}.filter.clear`, defaultMessage: "Clear filters" },
  loadFailed: { id: `${scope}.error.loadFailed`, defaultMessage: "Could not load schedules" },
  emptyTitle: { id: `${scope}.empty.title`, defaultMessage: "No recurring schedules yet" },
  emptyHint: {
    id: `${scope}.empty.hint`,
    defaultMessage: "Add a schedule to have preventive work orders generated automatically.",
  },
  emptyFilteredTitle: { id: `${scope}.empty.filteredTitle`, defaultMessage: "No schedules match the filters" },
  emptyFilteredHint: { id: `${scope}.empty.filteredHint`, defaultMessage: "Try a different status or asset." },
  filterActiveOnly: { id: `${scope}.filter.activeOnly`, defaultMessage: "Active only" },
  filterInactiveOnly: { id: `${scope}.filter.inactiveOnly`, defaultMessage: "Inactive only" },
  columnId: { id: `${scope}.column.id`, defaultMessage: "ID" },
  columnTitle: { id: `${scope}.column.title`, defaultMessage: "Title" },
  columnAsset: { id: `${scope}.column.asset`, defaultMessage: "Asset" },
  columnFrequency: { id: `${scope}.column.frequency`, defaultMessage: "Frequency" },
  columnBasis: { id: `${scope}.column.basis`, defaultMessage: "Basis" },
  columnTool: { id: `${scope}.column.tool`, defaultMessage: "Tool" },
  everyNHours: { id: `${scope}.frequency.everyNHours`, defaultMessage: "Every {n} h" },
  everyNCycles: { id: `${scope}.frequency.everyNCycles`, defaultMessage: "Every {n} cycles" },
  columnNextRun: { id: `${scope}.column.nextRun`, defaultMessage: "Next run" },
  columnActive: { id: `${scope}.column.active`, defaultMessage: "Active" },
  gridEditAria: { id: `${scope}.grid.editAria`, defaultMessage: "Edit schedule" },
  gridDeleteAria: { id: `${scope}.grid.deleteAria`, defaultMessage: "Delete schedule" },
  gridResetAria: { id: `${scope}.grid.resetAria`, defaultMessage: "Counter and reset history" },

  /*
   * The reset drawer. What the shared button and the shared log panel say lives in
   * features/cmms/cmmsMessages.js — two features mount those. This is the drawer's own chrome.
   */
  resetDrawerCounter: { id: `${scope}.resetDrawer.counter`, defaultMessage: "Counter" },
  resetDrawerReadingFailed: {
    id: `${scope}.resetDrawer.readingFailed`,
    defaultMessage: "Could not load the current reading",
  },
  resetDrawerNoReading: {
    id: `${scope}.resetDrawer.noReading`,
    defaultMessage: "No reading yet. Consumption is collected by a scheduled job, so a schedule created moments ago has nothing to report.",
  },
  // Said out loud because it looks like a bug otherwise: a service satisfies every schedule
  // watching that tool, so entries appear here that nobody created from this drawer.
  resetDrawerToolHistoryNote: {
    id: `${scope}.resetDrawer.toolHistoryNote`,
    defaultMessage: "This is the tool's service history. A service booked by a work order, or by another schedule watching this tool, appears here too.",
  },
  resetDrawerOverdue: { id: `${scope}.resetDrawer.overdue`, defaultMessage: "overdue" },

  // delete + activation
  deleteTitle: { id: `${scope}.delete.title`, defaultMessage: "Delete schedule" },
  deleteMessage: { id: `${scope}.delete.message`, defaultMessage: "Delete \"{title}\"? This cannot be undone." },
  toastDeleted: { id: `${scope}.toast.deleted`, defaultMessage: "Schedule deleted" },
  toastActivated: { id: `${scope}.toast.activated`, defaultMessage: "Schedule activated" },
  toastDeactivated: { id: `${scope}.toast.deactivated`, defaultMessage: "Schedule deactivated" },

  // form dialog — scope
  fieldScope: { id: `${scope}.field.scope`, defaultMessage: "Applies to" },
  scopeAsset: { id: `${scope}.scope.asset`, defaultMessage: "Asset" },
  scopeTool: { id: `${scope}.scope.tool`, defaultMessage: "Tool" },
  fieldToolRequired: { id: `${scope}.field.toolRequired`, defaultMessage: "Tool *" },
  toolLockedHint: {
    id: `${scope}.field.toolLockedHint`,
    defaultMessage: "The tool cannot be changed after the schedule is created. Its service history belongs to the tool that ran the hours.",
  },

  /*
   * The two kinds are reset by different events, and saying so is the whole reason they can
   * share one form. An asset schedule's anchor is a plan and the sweep advances it; a tool
   * schedule's is a fact — when the tool was last serviced — and only a closing work order
   * moves it.
   *
   * Each now also names the third way, because this form used to carry a date picker that looked
   * like one and was not. Removing it without saying where resetting lives would leave the form
   * a dead end for exactly the person who came here to reset something.
   */
  anchorResetOnGenerate: {
    id: `${scope}.field.anchorResetOnGenerate`,
    defaultMessage: "Counting restarts when the work order is created, or when the counter is reset from the schedule list.",
  },
  anchorResetOnClose: {
    id: `${scope}.field.anchorResetOnClose`,
    defaultMessage: "Counting restarts when the work order is closed — not when it is created — or when the counter is reset from the schedule list.",
  },
  fieldWarnAtPercent: { id: `${scope}.field.warnAtPercent`, defaultMessage: "Warn at (%)" },

  // form dialog
  fieldType: { id: `${scope}.field.type`, defaultMessage: "Type" },
  fieldPriority: { id: `${scope}.field.priority`, defaultMessage: "Priority" },
  fieldTriggerBasis: { id: `${scope}.field.triggerBasis`, defaultMessage: "Trigger basis" },
  fieldFrequency: { id: `${scope}.field.frequency`, defaultMessage: "Frequency" },
  fieldDayOfWeek: { id: `${scope}.field.dayOfWeek`, defaultMessage: "Day of week" },
  fieldRunningTimeInterval: { id: `${scope}.field.runningTimeInterval`, defaultMessage: "Running time interval (hours)" },
  fieldQuantityInterval: { id: `${scope}.field.quantityInterval`, defaultMessage: "Quantity interval (cycles)" },
  // fieldIntervalStartingDate and intervalStartingDateHint were removed with the picker they
  // labelled. It sent a property neither request view model declares, so it had never done
  // anything on create or on edit — and an editable anchor is the silent reset FE-15.20 replaced
  // with an explicit one that writes a record.
  addDialogTitle: { id: `${scope}.form.addTitle`, defaultMessage: "Add recurring schedule" },
  editDialogTitle: { id: `${scope}.form.editTitle`, defaultMessage: "Edit recurring schedule" },
  fieldTitleRequired: { id: `${scope}.field.titleRequired`, defaultMessage: "Title *" },
  fieldDescription: { id: `${scope}.field.description`, defaultMessage: "Description" },
  fieldAssetRequired: { id: `${scope}.field.assetRequired`, defaultMessage: "Asset *" },
  fieldAssignedDepartment: { id: `${scope}.field.assignedDepartment`, defaultMessage: "Assigned department" },
  fieldTrade: { id: `${scope}.field.trade`, defaultMessage: "Trade" },
  // A retired trade is only offered when the schedule already has it.
  fieldTradeRetired: { id: `${scope}.field.tradeRetired`, defaultMessage: "{name} (inactive)" },
  estimatedHoursHint: {
    id: `${scope}.field.estimatedHoursHint`,
    defaultMessage: "Every work order this schedule creates starts with this estimate.",
  },
  tradeNotifyHint: {
    id: `${scope}.field.tradeNotifyHint`,
    defaultMessage: "Work orders this schedule creates are announced to the trade's technicians. With no trade picked, the trades serving the department are told instead, and nobody if no trade serves it.",
  },
  fieldAssignedResource: { id: `${scope}.field.assignedResource`, defaultMessage: "Assigned resource" },
  fieldIntervalRequired: { id: `${scope}.field.intervalRequired`, defaultMessage: "Interval *" },
  fieldDayOfMonth: { id: `${scope}.field.dayOfMonth`, defaultMessage: "Day of month (1-31)" },
  fieldStartDateRequired: { id: `${scope}.field.startDateRequired`, defaultMessage: "Start date *" },
  fieldEndDate: { id: `${scope}.field.endDate`, defaultMessage: "End date" },
  // Read-only line under the pickers when editing a calendar schedule. The date is formatted by
  // cmmsFormat before it gets here, so the message only owns the label and the word order.
  nextRunReadOnly: { id: `${scope}.field.nextRunReadOnly`, defaultMessage: "Next run: {value}" },
  toastCreated: { id: `${scope}.toast.created`, defaultMessage: "Schedule created" },
  toastUpdated: { id: `${scope}.toast.updated`, defaultMessage: "Schedule updated" },

  // validation
  titleRequired: { id: `${scope}.validation.titleRequired`, defaultMessage: "Title is required" },
  assetRequired: { id: `${scope}.validation.assetRequired`, defaultMessage: "Asset is required" },
  startDateRequired: { id: `${scope}.validation.startDateRequired`, defaultMessage: "Start date is required" },
  intervalMin: { id: `${scope}.validation.intervalMin`, defaultMessage: "Interval must be at least 1" },
  intervalRequired: { id: `${scope}.validation.intervalRequired`, defaultMessage: "Interval must be greater than 0" },
  toolRequired: { id: `${scope}.validation.toolRequired`, defaultMessage: "Tool is required" },
  warnRange: { id: `${scope}.validation.warnRange`, defaultMessage: "Warn at must be 1-100" },
  dayOfMonthRange: { id: `${scope}.validation.dayOfMonthRange`, defaultMessage: "Day of month must be 1-31" },
  saveFailed: { id: `${scope}.error.saveFailed`, defaultMessage: "Could not save schedule" },
});
